import { createContext, useContext, useCallback, useMemo, useId, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { TabItem, TabsContextValue, TabsProps } from './types'

const TabsContext = createContext<TabsContextValue | null>(null)

/** Hook para acessar o contexto de abas. */
// eslint-disable-next-line react-refresh/only-export-components -- hook exportado junto ao provider (padrão de contexto)
export function useTabsContext(): TabsContextValue {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('useTabsContext must be used within a Tabs provider')
  }
  return context
}

interface TabsProviderProps extends Pick<TabsProps, 'defaultTab' | 'value' | 'onChange' | 'syncWithUrl' | 'basePath' | 'id'> {
  children: React.ReactNode
}

export function TabsProvider({
  children,
  defaultTab,
  value: controlledValue,
  onChange,
  syncWithUrl = false,
  basePath = '',
  id,
}: TabsProviderProps) {
  const generatedId = useId()
  const tabsId = id || generatedId
  const navigate = useNavigate()
  const params = useParams()
  
  // Estado interno para abas registradas
  const [tabs, setTabs] = useState<TabItem[]>([])
  
  // Determina o valor inicial baseado na URL ou defaultTab
  const getInitialTab = useCallback(() => {
    if (syncWithUrl && params.tab) {
      return params.tab
    }
    return defaultTab || ''
  }, [syncWithUrl, params.tab, defaultTab])
  
  // Estado interno (não controlado)
  const [internalTab, setInternalTab] = useState(getInitialTab)
  
  // Valor atual (controlado ou não)
  const activeTab = controlledValue !== undefined ? controlledValue : internalTab
  
  // Registra uma aba
  const registerTab = useCallback((tab: TabItem) => {
    setTabs((prev) => {
      if (prev.some((t) => t.value === tab.value)) {
        return prev
      }
      return [...prev, tab]
    })
  }, [])
  
  // Remove registro de uma aba
  const unregisterTab = useCallback((value: string) => {
    setTabs((prev) => prev.filter((t) => t.value !== value))
  }, [])
  
  // Define a aba ativa
  const setActiveTab = useCallback(
    (value: string) => {
      if (!controlledValue) {
        setInternalTab(value)
      }
      
      // Sincroniza com URL se necessário
      if (syncWithUrl && basePath) {
        navigate(`${basePath}/${value}`)
      }
      
      onChange?.(value)
    },
    [controlledValue, syncWithUrl, basePath, navigate, onChange]
  )
  
  // Se não há aba ativa e há abas registradas, seleciona a primeira
  // Isso é feito via useEffect no componente Tabs para evitar loops
  
  const contextValue = useMemo<TabsContextValue>(
    () => ({
      activeTab,
      setActiveTab,
      tabsId,
      registerTab,
      unregisterTab,
      tabs,
      syncWithUrl,
      basePath,
    }),
    [activeTab, setActiveTab, tabsId, registerTab, unregisterTab, tabs, syncWithUrl, basePath]
  )
  
  return <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>
}
